/**
 * MemberLocationMarker
 *
 * A remote workspace member's live-location marker: their avatar inside a
 * colored ring (per-member deterministic color) with an optional heading cone
 * pointing in the member's reported direction. Rendered inside a MapLibre
 * `<Marker>` whose container carries the shared z-index class.
 *
 * Reads THIS member's position via an atomic, id-keyed store selector so only
 * the moving member's marker re-renders when their fix updates — stationary
 * members are untouched.
 *
 * @module components/map/UserLocationLayer/parts/MemberLocationMarker
 */
import type { CSSProperties } from 'react';
import { memo, useMemo } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { Tooltip } from '@mantine/core';

import { UserAvatar } from '@/components/user/UserAvatar';
import { getAvatarColor } from '@/constants/roles';
import { useGeoMarkupStore } from '@/store';

import { HeadingCone } from './HeadingCone';
import styles from './MemberLocationMarker.module.css';

export interface MemberLocationMarkerProps {
  /** The member's user id — drives the deterministic ring/cone color + lookup. */
  userId: string;
  /** Display name for the avatar fallback + tooltip. */
  displayName: string;
  /** Optional avatar image URL. */
  avatarUrl?: string | null;
}

/**
 * Renders a remote member's position marker. Subscribes to only this member's
 * location slot, so a fix from another member does not re-render it. Renders
 * nothing until the member's position is known. Heading cone is only shown when
 * a numeric heading is available.
 */
export const MemberLocationMarker = memo(function MemberLocationMarker({
  userId,
  displayName,
  avatarUrl,
}: MemberLocationMarkerProps) {
  const location = useGeoMarkupStore(
    (state) => state.presence.memberLocations[userId]
  );
  const memberColor = getAvatarColor(userId);
  const ringStyle = useMemo<CSSProperties>(
    () => ({ '--member-color': memberColor }) as CSSProperties,
    [memberColor]
  );

  if (!location) {
    return null;
  }

  return (
    <Marker
      longitude={location.lng}
      latitude={location.lat}
      className="user-location-marker-container"
    >
      <Tooltip label={displayName} withArrow>
        <div className={styles.marker}>
          <HeadingCone heading={location.heading} color={memberColor} />
          <span className={styles.ring} style={ringStyle}>
            <UserAvatar
              displayName={displayName}
              avatarUrl={avatarUrl}
              size="sm"
            />
          </span>
        </div>
      </Tooltip>
    </Marker>
  );
});
